import {
  Box,
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { createPrismaSort, DataTable, DataTableSearch, useCrud } from '@pelicargo/datatable';
import { createColumnHelper } from '@tanstack/react-table';
import { uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { ContactFormDrawer } from '../components/Drawers/ContactFormDrawer';
import { trpc } from '../config/trpc';

type TableColumns = {
  id: number;
  full_name: string;
  title: string;
  phone_number: string;
  email: string;
  notes: string;
  associations: any[];
  action?: any;
};

export const ContactsScreen = () => {
  const contactDrawer = useDisclosure();

  const { query, handleQueryChange, sorting, setSorting, pagination, setPagination, take, skip } = useCrud();

  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);

  const { data, refetch } = trpc.searchContacts.useQuery({
    skip,
    take,
    search: query,
    orderBy: createPrismaSort(sorting),
  });
  const count = data?.[0];

  const onRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedContactId(row?.id);
      contactDrawer.onOpen();
    },
    [contactDrawer],
  );

  const onCloseCallback = () => {
    setSelectedContactId(null);
    refetch();
    contactDrawer.onClose();
  };

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        minSize: 0,
        size: 0,
      }),
      columnHelper.accessor('full_name', {
        size: Number.MIN_SAFE_INTEGER,
        header: 'Full Name',
      }),
      columnHelper.accessor('email', {
        header: 'Email',
      }),
      columnHelper.accessor('phone_number', {
        header: 'Phone Number',
      }),

      // The next three columns need an id: tag, otherwise they will spam the developer console of the browser with
      // hundreds of multi-paragraph "Encountered two children with the same key" errors.
      // https://tanstack.com/table/v8/docs/guide/column-defs#unique-column-ids
      columnHelper.accessor('associations', {
        header: 'Tags',
        id: 'associations_tags',
        cell: (cell) => {
          const allTags = [];
          cell.row.original?.associations?.map((node) => node?.tags?.map((n) => allTags.push(n)));

          return uniq(allTags).join(', ');
        },
      }),
      columnHelper.accessor('associations', {
        header: 'Airports',
        id: 'associations_airports',
        cell: (cell) => {
          const allAirports = [];
          cell.row.original?.associations?.map((node) => node?.airports?.map((n) => allAirports.push(n?.id)));

          return uniq(allAirports).join(', ');
        },
      }),
      columnHelper.accessor('associations', {
        header: 'Airlines',
        id: 'associations_providers',
        cell: (cell) => {
          const allAirlines = [];
          cell.row.original?.associations?.map((node) => node?.providers?.map((n) => allAirlines.push(n?.id)));

          return uniq(allAirlines).join(', ');
        },
      }),

      columnHelper.accessor('action', {
        header: '',
        minSize: 0,
        size: 0,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick(row)}>Update</MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, onRowClick],
  );

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" justify="space-between" zIndex="99" w="full">
          <Stack direction="row" align="center" w="full">
            <Heading variant="h3">Contacts</Heading>
            <DataTableSearch value={query} onChange={handleQueryChange} />
          </Stack>
          <Button colorScheme="brand" onClick={contactDrawer.onOpen}>
            Create Contact
          </Button>
        </Stack>
      </Stack>
      <DataTable
        columns={columns}
        data={data?.[1] ?? []}
        dataCount={count ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={onRowClick}
      />
      <ContactFormDrawer isOpen={contactDrawer.isOpen} onClose={onCloseCallback} contactId={selectedContactId} />
    </Stack>
  );
};
